import { subscribeToConsent } from './consent-manager';
import type { ConsentState } from './types';

// --------- TYPES ---------
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

type ConsentValue = 'granted' | 'denied';

type GTMConsentParams = {
  analytics_storage: ConsentValue;
  functionality_storage: ConsentValue;
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
};

// --------- HELPERS ---------
function ensureDataLayer(): void {
  window.dataLayer = window.dataLayer || [];
}

function ensureGtag(): void {
  if (typeof window.gtag !== 'function') {
    window.gtag = function (...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }
}

function buildConsentParams(state: ConsentState): GTMConsentParams {
  const analyticsGranted =
    state.status === 'decided' && state.record.categories.analytics;

  return {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    functionality_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  };
}

// --------- CORE FUNCTIONS ---------
// Default consent is now set inline in GTMScript.tsx before GTM loads.
// This bridge only handles updates when the user makes a choice.
function updateConsent(state: ConsentState): void {
  ensureDataLayer();
  ensureGtag();
  window.gtag('consent', 'update', buildConsentParams(state));

  window.dataLayer.push({
    event: 'consent_update',
    consent_state: state.status,
    analytics_granted:
      state.status === 'decided' && state.record.categories.analytics,
  });
}

// --------- PUBLIC API ---------
let _initialized = false;

export function initGTMBridge(): void {
  // Guard against double init — React Strict Mode runs effects twice in dev
  if (_initialized) return;
  _initialized = true;

  // Subscribe to future consent changes so gtag('consent', 'update')
  // fires whenever the user accepts or declines in the banner
  subscribeToConsent((newState) => {
    updateConsent(newState);
  });
}
